import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { keysAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Key, 
  Search, 
  Calendar,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';

const Keys = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const limit = 20;

  const { data: keysData, isLoading } = useQuery(
    ['keys', { page, limit, search: searchTerm }],
    () => {
      const params = { page, limit };
      if (searchTerm) {
        params.search = searchTerm;
      }
      return keysAPI.getAll(params);
    },
    {
      select: (response) => response.data.data,
      keepPreviousData: true
    }
  );

  const keys = keysData?.keys || [];
  const pagination = keysData?.pagination || {};

  // Backend now handles filtering, so we use keys directly
  const filteredKeys = keys;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: 'badge-success',
      pending: 'badge-warning',
      failed: 'badge-danger',
      processing: 'badge-info'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleKeyClick = (key) => {
    setSelectedKey(key);
    setShowModal(true);
    setIsOpening(false);
    // Trigger fade in on next frame
    setTimeout(() => setIsOpening(true), 10);
  };

  const handleOrderClick = (orderNumber) => {
    setIsClosing(true);
    setTimeout(() => {
      setShowModal(false);
      setIsClosing(false);
      setIsOpening(false);
      navigate(`/orders/${orderNumber}`);
    }, 200);
  };

  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowModal(false);
      setIsClosing(false);
      setIsOpening(false);
    }, 200);
  };

  if (isLoading && page === 1) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      {/* Filters */}
      <div className="keys-search-container">
      <div className="card keys-search-box">
        <div className="card-content">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search keys, products, order IDs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg text-gray-100 placeholder-gray-400"
                  style={{ 
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #4a4a4a',
                    outline: 'none',
                    transition: 'box-shadow 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 2px #3b198e';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = '';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Keys Table */}
      {filteredKeys.length === 0 ? (
        <div className="text-center py-12">
          <Key className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-white">No keys found</h3>
          <p className="mt-1 text-sm text-white">
            {searchTerm 
              ? 'Try adjusting your search criteria.'
              : 'Keys will appear here once orders are processed.'
            }
          </p>
        </div>
      ) : (
        <div className="keys-list-container">
        <div className="card keys-list-box">
          <div style={{ padding: '16px', paddingBottom: '0' }}>
            <div style={{ maxHeight: 'calc(8 * 80px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: '8px' }} className="custom-scrollbar">
            <table className="table" style={{ borderCollapse: 'separate', borderSpacing: '0 8px', width: '100%' }}>
              <thead>
                <tr style={{ borderBottom: 'none' }}>
                  <th style={{ border: 'none', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '16px', textTransform: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Key className="h-7 w-7" style={{ color: '#3b198e' }} />
                      <span style={{ fontSize: '22px', fontWeight: '700', color: '#ffffff' }}>Keys</span>
                    </div>
                  </th>
                  <th style={{ textAlign: 'right', border: 'none' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredKeys.map((key, index) => (
                  <tr 
                    key={`${key.id}-${index}`} 
                    className="transition-colors" 
                    style={{ 
                      cursor: 'pointer', 
                      outline: selectedRowId === `${key.id}-${index}` ? '2px solid #3b198e' : 'none',
                      outlineOffset: '-2px',
                      backgroundColor: '#1a1a1a',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }} 
                    onClick={() => {
                      setSelectedRowId(`${key.id}-${index}`);
                      handleKeyClick(key);
                    }} 
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'} 
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a1a1a'}
                  >
                    <td style={{ border: 'none', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '16px', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        {/* Deliverable */}
                        <div style={{ 
                          color: '#ffffff', 
                          fontSize: '15px', 
                          fontFamily: 'monospace',
                          fontWeight: '500',
                          wordBreak: 'break-all',
                          maxWidth: '400px'
                        }}>
                          {key.key_value || 'Processing...'}
                        </div>
                        
                        {/* Product Name */}
                        <div style={{ 
                          color: '#d1d5db', 
                          fontSize: '14px'
                        }}>
                          {key.product_name}
                        </div>
                        
                        {/* Variant - Order Link */}
                        <div style={{ 
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ color: '#9ca3af' }}>{key.variant_name}</span>
                          <span style={{ color: '#9ca3af' }}>-</span>
                          <span 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOrderClick(key.order_number);
                            }}
                            style={{ 
                              color: '#3b198e',
                              cursor: 'pointer',
                              textDecoration: 'none',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                          >
                            {key.order_number}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td style={{ border: 'none', color: '#9ca3af', textAlign: 'right', paddingRight: '16px', borderTopRightRadius: '8px', borderBottomRightRadius: '8px', fontSize: '14px' }}>
                      {format(new Date(key.created_at + 'Z'), 'MMM dd, yyyy')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
            <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ 
                    opacity: page === 1 ? 0.5 : 1,
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    minWidth: '40px',
                    minHeight: '40px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {/* Page Number Tabs */}
                <div className="flex gap-1">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      style={{
                        minWidth: '40px',
                        minHeight: '40px',
                        backgroundColor: pageNum === page ? '#2d1470' : 'transparent',
                        color: pageNum === page ? '#ffffff' : '#9ca3af',
                        border: 'none',
                        fontWeight: pageNum === page ? '600' : '400',
                        borderRadius: '50%',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (pageNum !== page) {
                          e.currentTarget.style.color = '#ffffff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (pageNum !== page) {
                          e.currentTarget.style.color = '#9ca3af';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  style={{ 
                    opacity: page === pagination.totalPages ? 0.5 : 1,
                    cursor: page === pagination.totalPages ? 'not-allowed' : 'pointer',
                    minWidth: '40px',
                    minHeight: '40px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            
            {/* Total Keys Counter */}
            <div className="text-sm text-white">
              Total Keys: {pagination.total || 0}
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Key Details Modal */}
      {(showModal || isClosing) && selectedKey && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            opacity: isClosing ? 0 : (isOpening ? 1 : 0),
            transition: 'opacity 0.2s ease-out'
          }}
          onClick={handleCloseModal}
        >
          <div 
            style={{
              opacity: isClosing ? 0 : (isOpening ? 1 : 0),
              transform: isClosing ? 'scale(0.95)' : (isOpening ? 'scale(1)' : 'scale(0.95)'),
              transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
              backgroundColor: '#1a1a1a',
              border: '1px solid #4a4a4a',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Key Details
              </h3>
              <button
                onClick={handleCloseModal}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Key Details - Stacked Vertically */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Deliverable/Key */}
              <div>
                <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Deliverable
                </label>
                <div style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}>
                  {selectedKey.key_value || 'N/A'}
                </div>
              </div>

              {/* Product */}
              <div>
                <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Product
                </label>
                <div style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}>
                  {selectedKey.product_name} ({selectedKey.variant_name})
                </div>
              </div>

              {/* Order ID (Clickable) */}
              <div>
                <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Order ID
                </label>
                <div 
                  onClick={() => handleOrderClick(selectedKey.order_number)}
                  style={{
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #4a4a4a',
                    borderRadius: '6px',
                    padding: '12px',
                    color: '#3b198e',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(59, 25, 142, 0.1)';
                    e.currentTarget.style.borderColor = '#3b198e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0a0a0a';
                    e.currentTarget.style.borderColor = '#4a4a4a';
                  }}
                >
                  {selectedKey.order_number}
                </div>
              </div>

              {/* Date */}
              <div>
                <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Date
                </label>
                <div style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}>
                  {format(new Date(selectedKey.created_at + 'Z'), 'MMM dd, yyyy h:mm:ss a')}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Keys;
